import { useCallback, useEffect, useMemo, useState } from "react";
import { listServiceCheckIns, recordServiceCheckIn } from "@/lib/service/client";
import { uploadServiceCheckInEvidence } from "@/lib/service/checkInEvidenceClient";
import type { ServiceCheckIn, ServiceCheckInMilestone } from "@/lib/service/types";

export function useServiceEventCheckIns(eventId: string | null, onRecorded: () => void) {
  const [checkIns, setCheckIns] = useState<ServiceCheckIn[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [evidenceBusy, setEvidenceBusy] = useState(false);
  const [pendingEvidenceUrl, setPendingEvidenceUrl] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!eventId) {
      setCheckIns([]);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await listServiceCheckIns(eventId);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      setCheckIns([]);
      return;
    }
    setCheckIns(res.data.check_ins);
  }, [eventId]);

  useEffect(() => {
    void reload();
    setNotes("");
    setPendingEvidenceUrl(null);
  }, [reload, eventId]);

  const latestByMilestone = useMemo(() => {
    const map = new Map<ServiceCheckInMilestone, ServiceCheckIn>();
    for (const row of checkIns) {
      map.set(row.milestone, row);
    }
    return map;
  }, [checkIns]);

  const recordMilestone = useCallback(
    async (milestone: ServiceCheckInMilestone) => {
      if (!eventId) return false;
      setBusy(true);
      setError(null);
      const res = await recordServiceCheckIn({
        event_id: eventId,
        milestone,
        notes: notes.trim() || undefined,
        evidence_url: pendingEvidenceUrl,
      });
      setBusy(false);
      if (!res.ok) {
        setError(res.error);
        return false;
      }
      setNotes("");
      setPendingEvidenceUrl(null);
      await reload();
      onRecorded();
      return true;
    },
    [eventId, notes, pendingEvidenceUrl, onRecorded, reload]
  );

  const uploadEvidence = useCallback(
    async (file: File) => {
      if (!eventId) return;
      setEvidenceBusy(true);
      setError(null);
      const res = await uploadServiceCheckInEvidence(eventId, file);
      setEvidenceBusy(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setPendingEvidenceUrl(res.public_url);
    },
    [eventId]
  );

  return {
    checkIns,
    latestByMilestone,
    loading,
    busy,
    evidenceBusy,
    error,
    notes,
    setNotes,
    pendingEvidenceUrl,
    recordMilestone,
    uploadEvidence,
    reload,
  };
}
